/* eslint-disable react/no-danger */
import React, { createRef, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { pdfjs } from 'react-pdf';
import { Helmet } from 'react-helmet';
import { Box, Stack } from '@mui/material';
import moment from 'moment';

import {
  ISubTopicFAQ,
  ISubTopicReview,
  getGroupedSubTopics,
  getSubTopicById,
  getSubTopicState,
} from '../../../store/SubTopicSlice';
import { useAppSelector } from '../../../hooks';
import MobileHeader from '../../../components/Mobile/Header';
import PdfViewerHeader from '../../../components/PdfViewer/PdfViewerHeader';
import PdfViewer from '../../../components/PdfViewer/PdfViewer';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import PdfViewerModal from './modals/PdfViewerModal';
import TopicHeader from '../../../components/Headers/TopicHeader';
import WatchVideosModal from './modals/WatchVideosModal';
import Session from './Session';
import FAQSection from './components/FAQSection';
import ReviewSection from './components/ReviewSection';
import {
  createBookmark,
  getBookmark,
  getBookmarkState,
} from '../../../store/BookmarkSlice';
import { getClassFromLocalStorage, isMobileScreen } from '../../../helper';
import { TP_COLORS } from '../../../constants';
import {
  getSessionState,
  resetSession,
  selectStudent,
} from '../../../store/SessionSlice';
import socket from '../../../helper/Socket';
import { getAuthState } from '../../../store/AuthSlice';
import { saveStudentProgress } from '../../../store/ProgressSlice';
import { saveUserUsage } from '../../../store/AccessLimitSlice/AccessLimitApi';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../store/AccessLimitSlice';
import { getMetaTagState } from '../../../store/MetaTagSlice';

export interface ISessionUser {
  _id: string;
  firstname: string;
  lastname: string;
  username: string;
  autoConnectDisabled?: boolean;
  isOnline: boolean;
}

interface IState {
  initialLoading: boolean;
  pageNo: string;
  fileUrl: string;
  totalPages: number;
  scale: number;
  isViewPdfViewerModalOpen: boolean;
  studentId?: string;
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export const scales = [0.25, 0.5, 0.75, 1, 1.5, 2, 2.5, 3];

const initialState: IState = {
  initialLoading: true,
  pageNo: '1',
  fileUrl: '',
  totalPages: 0,
  scale: scales[3],
  isViewPdfViewerModalOpen: false,
};

const Resources: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const divRef: any = createRef();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const bookmarkState = useAppSelector(getBookmarkState);
  const sessionState = useAppSelector(getSessionState);
  const authState = useAppSelector(getAuthState);
  const accessLimitState = useAppSelector(getAccessLimitState);
  const metaTagState = useAppSelector(getMetaTagState);

  // State
  const [state, setState] = useState(initialState);
  const [watchVideosModalOpen, setWatchVideosModalOpen] = useState(false);
  const [windowSize, setWindowSize] = useState<number | undefined>(undefined);
  const [isWhiteboardVisible, setIsWhiteboardVisible] = useState(false);
  const [containerMaxWidth, setContainerMaxWidth] = useState<
    number | undefined
  >(undefined);
  const [relatedStudents, setRelatedStudents] = useState<ISessionUser[]>([]);

  const subTopicData = subTopicState.subTopic;
  const urlStringPattern = params.pattern || '';
  const urlStringPatternDivided = urlStringPattern.split('_').reverse();
  const subTopicId = urlStringPatternDivided[0] || '';
  const isLoading = state.initialLoading;
  const topicData =
    subTopicData && subTopicData.topic ? subTopicData.topic : undefined;

  const hasVideos =
    subTopicData &&
    subTopicData.videoLinks &&
    subTopicData.videoLinks.length > 0;

  const bookmarkData = bookmarkState.bookmark;
  const authUser = authState.user;
  const { usage, limitation } = accessLimitState;
  const classData = getClassFromLocalStorage();
  const userRole = authState.user?.role.name;
  const { session } = sessionState;

  const isPremiumModal =
    authUser &&
    (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted);

  const pdfContainerStyles: React.CSSProperties = {
    margin: '0 15px',
    padding: '30px 0',
    minHeight: '100vh',
    maxWidth: containerMaxWidth,
    overflow: 'auto',
    outline: 'none',
  };

  let maxWidthForPdfViewer = containerMaxWidth;
  let curriculum = '';
  let subject = '';
  let subjectCode = '';
  let topicName = '';
  let subTopicName = '';
  const currentYear = new Date().getFullYear() || '';

  let metaTitle = `Tutorials with Questions and Answers ${currentYear}- Tutopiya`;

  let metaDesc = `Free Tutorials with revision questions and answers. Unravel the complexities with our curriculum and subject lessons in ${currentYear}.`;

  if (subTopicData && topicData) {
    curriculum = topicData.curriculum || '';
    subject = topicData.subject || '';
    subjectCode = topicData.subjectCode || '';
    topicName = topicData.name || '';
    subTopicName = subTopicData.name || '';

    if (curriculum && subject && subjectCode && topicName && subTopicName) {
      metaTitle = `${curriculum} (${subjectCode}) ${subTopicName} ${subject} Tutorials with Questions and Answers ${currentYear}- Tutopiya`;

      metaDesc = `Free Tutorials on ${subTopicName} in ${subject} with revision questions and answers. Unravel the complexities of ${topicName} with our ${curriculum} ${subjectCode} ${subject} lessons in ${currentYear}`;
    }
  }

  if (containerMaxWidth) {
    if (containerMaxWidth > 900) {
      maxWidthForPdfViewer = 900;
    } else if (containerMaxWidth < 900 && !isMobileScreen()) {
      maxWidthForPdfViewer = containerMaxWidth - 100;
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);

    handleWindowResize();

    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    resizePdfViewer();

    setTimeout(() => {
      resizePdfViewer();
    }, 500);
  }, [divRef]);

  useEffect(() => {
    if (subTopicId) {
      dispatch(getGroupedSubTopics({ subTopicId }));
      dispatch(getSubTopicById(subTopicId));
    }
  }, [subTopicId]);

  useEffect(() => {
    if (
      subTopicState.subTopic &&
      subTopicState.subTopic.resources &&
      subTopicState.subTopic.resources.length > 0
    ) {
      const resource = subTopicState.subTopic.resources[0];

      setState({
        initialLoading: false,
        fileUrl: resource.webContentLink || '',
        pageNo:
          subTopicData?._id === bookmarkData.subTopicId
            ? bookmarkData.pageNo
            : '1',
        totalPages: 0,
        scale: scales[3],
        isViewPdfViewerModalOpen: false,
      });

      if (
        userRole === 'Tutor' &&
        classData &&
        classData.tutorId &&
        classData.studentId &&
        classData.end &&
        classData.meetingId &&
        moment(classData.end).add(30, 'minutes') > moment()
      ) {
        dispatch(
          createBookmark({
            tutorId: classData.tutorId,
            studentId: classData.studentId,
            subject,
            subTopicId,
            subTopicName: subTopicData?.name || '',
            pageNo: '1',
            ...(moment(classData.end) < moment()
              ? { meetingId: classData.meetingId }
              : {}),
          })
        );
      }

      if (
        classData &&
        classData.tutorId &&
        classData.studentId &&
        !bookmarkData.pageNo
      ) {
        dispatch(
          getBookmark({
            tutorId: classData.tutorId,
            studentId: classData.studentId,
            subject: classData.subject,
          })
        );
      }
    }
  }, [subTopicState.subTopic]);

  useEffect(() => {
    if (bookmarkData.pageNo) {
      setState((prevState) => ({
        ...prevState,
        pageNo: bookmarkData.pageNo,
      }));
    }
  }, [bookmarkData.pageNo]);

  useEffect(() => {
    socket.on('receive_students', (data) => {
      if (Array.isArray(data)) {
        setRelatedStudents(data);
      }
    });

    socket.on('disconnect_student', (data) => {
      if (
        data.student &&
        userRole === 'Tutor' &&
        getOnlineStudent(data.student)
      ) {
        setRelatedStudents((prevState) => {
          const studentList = [...prevState];
          const studentIndex = studentList.findIndex(
            (item) => item._id === data.student._id
          );
          if (studentIndex !== -1) {
            studentList[studentIndex].isOnline = false;
          }
          if (studentList.length === 0) {
            dispatch(resetSession());
            dispatch(selectStudent(''));
          }
          return studentList;
        });
      }
    });

    socket.on('connect_student', (data) => {
      if (
        data.student &&
        userRole === 'Tutor' &&
        getOnlineStudent(data.student)
      ) {
        setRelatedStudents((prevState) => {
          const studentList = [...prevState];
          const index = studentList.findIndex(
            (item) => item._id === data.student._id
          );
          if (index !== -1) {
            studentList[index].isOnline = true;
            studentList[index].autoConnectDisabled = true;
          }
          return studentList;
        });
      }
    });
  }, [socket]);

  useEffect(() => {
    if (session) {
      setState((prevState) => ({
        ...prevState,
        pageNo:
          prevState.studentId !== session.student ? '1' : prevState.pageNo,
        studentId: session.student,
      }));
    }
  }, [session]);

  const handleWindowResize = () => {
    const windowInnerWidth = window.innerWidth;

    if (windowSize !== windowInnerWidth) {
      setWindowSize(window.innerWidth);
    }
  };

  const resizePdfViewer = () => {
    const { current } = divRef;

    if (current) {
      const boundingRect = current.getBoundingClientRect();

      if (boundingRect.width !== containerMaxWidth) {
        setContainerMaxWidth(boundingRect.width);
      }
    }
  };

  const togglePdfViewerModal = () => {
    setState((prevState) => ({
      ...prevState,
      isViewPdfViewerModalOpen: !prevState.isViewPdfViewerModalOpen,
    }));
  };

  const toggleWatchVideoModal = () => {
    if (
      !watchVideosModalOpen &&
      limitation &&
      limitation.videoWatches &&
      limitation.videoWatches > 0 &&
      usage.videoWatches >= limitation.videoWatches
    ) {
      dispatch(isPremiumModal ? togglePremiumModal() : toggleLeadGenModal());
    } else {
      setWatchVideosModalOpen(!watchVideosModalOpen);
    }
  };

  const onDocumentLoadSuccess = (numPages) => {
    setState((prevState) => ({
      ...prevState,
      initialLoading: false,
      totalPages: numPages,
    }));
  };

  const onChangePage = (pageNo: string) => {
    const isNext = Number(state.pageNo) < Number(pageNo);
    const isSkipped =
      topicName.toLocaleLowerCase().search('overview') < 0 &&
      subTopicName.toLocaleLowerCase().search('overview') < 0 &&
      subTopicName.toLocaleLowerCase().search('planner') < 0 &&
      topicName.toLocaleLowerCase().search('planner') < 0;

    if (
      isNext &&
      isSkipped &&
      limitation &&
      limitation.slideViews &&
      limitation.slideViews > 0 &&
      usage.slideViews + 2 >= limitation.slideViews
    ) {
      dispatch(isPremiumModal ? togglePremiumModal() : toggleLeadGenModal());
    } else {
      setState((prevState) => ({
        ...prevState,
        pageNo,
      }));

      // Save Resource Progress
      if (userRole === 'Student' && Number(state.pageNo) < Number(pageNo)) {
        dispatch(
          saveStudentProgress({
            subTopicId,
            resourceCompletion:
              (Number(pageNo) / (state.totalPages + session.questions.length)) *
              100,
          })
        );
      }

      if (
        isNext &&
        isSkipped &&
        authUser &&
        (userRole === 'Guest' || userRole === 'Student')
      ) {
        dispatch(
          saveUserUsage({
            userId: authUser._id,
            option: 'slideViews',
          })
        );
      }

      if (
        userRole === 'Tutor' &&
        classData &&
        classData.tutorId &&
        classData.studentId &&
        classData.end &&
        classData.meetingId &&
        moment(classData.end).add(30, 'minutes') > moment()
      ) {
        dispatch(
          createBookmark({
            tutorId: classData.tutorId,
            studentId: classData.studentId,
            subject,
            subTopicId,
            subTopicName: subTopicData?.name || '',
            pageNo,
            ...(moment(classData.end) < moment()
              ? { meetingId: classData.meetingId }
              : {}),
          })
        );
      }
    }
  };

  const onAttepmt = () => {
    navigate(location.pathname.replace('/resources/', '/'));
  };

  const handleScaleChange = (type: 'inc' | 'dec') => {
    const _index = scales.findIndex((sc) => sc === state.scale);

    if (
      type === 'inc' &&
      state.scale !== scales[scales.length - 1] &&
      _index > -1
    ) {
      setState((prevState) => ({
        ...prevState,
        scale: scales[_index + 1],
      }));
    } else if (type === 'dec' && state.scale !== scales[0] && _index > -1) {
      setState((prevState) => ({
        ...prevState,
        scale: scales[_index - 1],
      }));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      e.keyCode === 39 &&
      Number(state.pageNo) < state.totalPages + session.questions.length + 1
    ) {
      onChangePage((Number(state.pageNo) + 1).toString());
    } else if (e.keyCode === 37 && Number(state.pageNo) > 1) {
      onChangePage((Number(state.pageNo) - 1).toString());
    }
  };

  const onChangeStudent = (studentId: string) => {
    if (studentId) {
      socket.emit('select_student', { studentId, subTopicId });
    } else {
      dispatch(resetSession());
    }
  };

  const getOnlineStudent = (student: ISessionUser) => {
    const studentsData = authState.user?.students || [];
    return studentsData.find((item) => item.username === student.username);
  };

  if (
    subTopicState.subTopic &&
    ((subTopicState.subTopic.resources &&
      subTopicState.subTopic.resources.length === 0) ||
      !subTopicState.subTopic.resources)
  )
    navigate(location.pathname.replace('/resources/', '/'));

  return (
    <div ref={divRef}>
      {subTopicData && subTopicData.FAQs?.length ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: getFAQStructuredData(subTopicData.FAQs),
          }}
        />
      ) : null}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: getReviewStructuredData(subTopicData?.reviews || []),
        }}
      />
      {!metaTagState.metaTags ||
        (!metaTagState.metaTags[location.pathname.replace(/[/]$/, '')] && (
          <Helmet>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDesc} />
          </Helmet>
        ))}
      <MobileHeader
        leftContents={{
          type: 'subtopic-nav-bar',
        }}
      />
      <TopicHeader
        subject={subject}
        topic={topicName}
        subTopics={[subTopicName]}
        isGetSubTopic={false}
        {...(userRole === 'Tutor' && !isLoading
          ? { relatedStudents, onChangeStudent }
          : {})}
      />
      {subTopicState.status === 'loading' ? (
        <Stack
          justifyContent="center"
          style={{ minHeight: 'calc(100vh - 64px)' }}
        >
          <BasicLoader />
        </Stack>
      ) : (
        <>
          {!isLoading && state.fileUrl && (
            <>
              <Session subTopicId={subTopicId} />
              <PdfViewerHeader
                allowWatchVideo={hasVideos}
                onChangePage={onChangePage}
                totalPages={state.totalPages + session.questions.length}
                pageNo={state.pageNo}
                scale={state.scale}
                onScaleChange={handleScaleChange}
                onFullScreenToggle={togglePdfViewerModal}
                toggleWatchVideoModal={toggleWatchVideoModal}
                fileUrl={state.fileUrl}
                isWhiteboardVisible={isWhiteboardVisible}
                setIsWhiteboardVisible={setIsWhiteboardVisible}
                maxWidth={
                  containerMaxWidth ? `${containerMaxWidth}px` : undefined
                }
                appBarProps={{
                  sx: {
                    position: { xs: 'static', sm: 'sticky' },
                    top: { xs: 'unset', sm: '64px' },
                    zIndex: 5,
                  },
                }}
              />
              <Box
                tabIndex={0}
                onKeyDown={onKeyDown}
                style={{
                  ...pdfContainerStyles,
                  ...(isWhiteboardVisible ? { visibility: 'hidden' } : {}),
                }}
              >
                <div
                  style={{
                    minHeight: maxWidthForPdfViewer
                      ? maxWidthForPdfViewer / 2
                      : 300,
                  }}
                >
                  <PdfViewer
                    fileUrl={state.fileUrl}
                    scale={state.scale}
                    selectedPageNumber={state.pageNo}
                    onLoadSuccess={(numPages) =>
                      onDocumentLoadSuccess(numPages)
                    }
                    onAttempt={onAttepmt}
                    pageWidth={
                      maxWidthForPdfViewer
                        ? maxWidthForPdfViewer - 60
                        : maxWidthForPdfViewer
                    }
                    session={session}
                    onChangePage={onChangePage}
                  />
                </div>

                {/* SubTopic Contents Section */}
                {state.totalPages > 0 && subTopicData?.contents?.length ? (
                  <div style={{ padding: '30px 30px 0px 30px' }}>
                    {subTopicData.contents.map((content) => (
                      <div
                        key={content._id}
                        style={{
                          border: '1px solid #D0D0D052',
                          borderRadius: '7px',
                          margin: '10px',
                          backgroundColor: TP_COLORS.white,
                        }}
                        className="view ql-editor ql-viewer"
                        dangerouslySetInnerHTML={{
                          __html: content.details,
                        }}
                      />
                    ))}
                  </div>
                ) : null}

                {/* SubTopic FAQ Section */}
                {state.totalPages > 0 && subTopicData?.FAQs?.length ? (
                  <FAQSection faqList={subTopicData.FAQs} />
                ) : null}

                {/* SubTopic Review Section */}
                {state.totalPages > 0 && subTopicData?.reviews?.length ? (
                  <ReviewSection reviewList={subTopicData.reviews} />
                ) : null}
              </Box>
            </>
          )}
        </>
      )}

      {/* Modals */}
      <PdfViewerModal
        open={state.isViewPdfViewerModalOpen}
        fileUrl={state.fileUrl}
        pageNumber={state.pageNo}
        allowWatchVideo={hasVideos}
        onClose={togglePdfViewerModal}
        toggleWatchVideoModal={() => {
          togglePdfViewerModal();
          toggleWatchVideoModal();
        }}
        onChangePageModal={onChangePage}
      />
      {watchVideosModalOpen && subTopicData ? (
        <WatchVideosModal
          open={watchVideosModalOpen}
          onClose={toggleWatchVideoModal}
          subTopic={subTopicData}
        />
      ) : null}
    </div>
  );
};

const getFAQStructuredData = (faqList: ISubTopicFAQ[]) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
  return JSON.stringify(schema);
};

const getReviewStructuredData = (reviewList: ISubTopicReview[]) => {
  const schema = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: 'Tutopiya',
    url: 'https://tutopiya.com/',
    logo: 'https://tutopiya.com/logo.png',
    description: 'Online Tutoring Platform',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      reviewCount: '1',
    },
    review: [
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'John Doe',
        },
        datePublished: '2023-11-20',
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5',
          worstRating: '1',
        },
        reviewBody:
          'I had a great experience with Tutopiya. The tutors are knowledgeable and supportive. Highly recommended!',
      },
    ],
  };
  if (reviewList.length) {
    schema.review = reviewList.map((reviewItem) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: reviewItem.reviewer || '',
      },
      datePublished: moment(reviewItem.createdAt).format('DD MMM YYYY'),
      reviewRating: {
        '@type': 'Rating',
        ratingValue: reviewItem.rating.toString(),
        bestRating: '5',
        worstRating: '1',
      },
      reviewBody: reviewItem.review,
    }));

    const aggregateRating =
      reviewList.reduce((accumulator, object) => {
        return accumulator + object.rating;
      }, 0) / reviewList.length;

    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: aggregateRating.toString(),
      reviewCount: reviewList.length.toString(),
    };
  }
  return JSON.stringify(schema);
};

export default Resources;
